import { useState } from "react";

import { Payment, Receipt } from "@/types/receipt";

import {
  formatCurrency,
  formatDate,
  translate,
  formatPaymentMethod,
} from "@/utils/formatters";

import { ExpandableButton } from "../common/ExpandableButton";

interface PaymentSectionProps {
  payment: Payment;
  index: number;
  receipt: Receipt;
  lang: string;
}

export const PaymentSection = ({
  payment,
  index,
  receipt,
  lang,
}: PaymentSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  const hasExpand =
    payment.payment_method ??
    payment.timestamp ??
    payment.payment_scheme ??
    payment.issuer_name ??
    payment.issuer_number ??
    payment.masked_pan ??
    payment.currency ??
    payment.payment_amount ??
    payment.tip_amount ??
    payment.card_acceptor_identifier ??
    payment.card_acceptor_terminal_identifier ??
    payment.authorization_identification_response ??
    payment.retrieval_reference_number ??
    payment.application_identifier ??
    payment.terminal_verification_result ??
    payment.transaction_status_information ??
    payment.transaction_certificate ??
    payment.authorization_response_code;

  return (
    <div className="box head" key={"payment-" + index}>
      <div className="split">
        <div>
          <ExpandableButton
            onClick={() => setExpanded(!expanded)}
            isExpanded={expanded}
            hasExpandableContent={!!hasExpand}
          >
            {translate(lang, "Betaling", "Payment")}
          </ExpandableButton>
        </div>

        {payment.payment_amount !== undefined && (
          <p className="amount article-divider">
            {formatCurrency(
              payment.payment_amount,
              receipt.total.merchant_currency,
              false,
              Boolean(payment.money_back)
            )}
          </p>
        )}
      </div>
      <div>
        {payment.payment_method != null && (
          <p className="amount small">
            {formatPaymentMethod(payment.payment_method)}
          </p>
        )}
        {payment.timestamp != null && (
          <p className="amount small">
            {formatDate(payment.timestamp, receipt.merchant.country_code)}
          </p>
        )}

        {payment.payment_scheme != null && (
          <p className="amount small">{payment.payment_scheme}</p>
        )}
        {payment.contactless === true && (
          <p className="amount small">
            {translate(lang, "Kontaktløs", "Contactless")}
          </p>
        )}
        {payment.issuer_name != null && (
          <p className="amount small">{payment.issuer_name}</p>
        )}
        {payment.issuer_number != null && (
          <p className="amount small">
            {translate(lang, "UTSTEDER: ", "ISSUER: ")}
            {payment.issuer_number}
          </p>
        )}

        {expanded && payment.masked_pan != null && (
          <p className="amount small">
            {translate(lang, "MASKED PAN: ", "MASKED PAN: ")}
            {payment.masked_pan}
          </p>
        )}

        {expanded && payment.card_acceptor_identifier != null && (
          <p className="amount small">
            CAID: {payment.card_acceptor_identifier}
          </p>
        )}
        {expanded && payment.card_acceptor_terminal_identifier != null && (
          <p className="amount small">
            CATID: {payment.card_acceptor_terminal_identifier}
          </p>
        )}
        {expanded && payment.authorization_identification_response != null && (
          <p className="amount small">
            AUTH CODE: {payment.authorization_identification_response}
          </p>
        )}
        {expanded && payment.retrieval_reference_number != null && (
          <p className="amount small">
            RRN: {payment.retrieval_reference_number}
          </p>
        )}
        {expanded && payment.application_identifier != null && (
          <p className="amount small">AID: {payment.application_identifier}</p>
        )}
        {expanded && payment.terminal_verification_result != null && (
          <p className="amount small">
            TVR: {payment.terminal_verification_result}
          </p>
        )}
        {expanded && payment.transaction_status_information != null && (
          <p className="amount small">
            TSI: {payment.transaction_status_information}
          </p>
        )}
        {expanded && payment.transaction_certificate != null && (
          <p className="amount small">TC: {payment.transaction_certificate}</p>
        )}
        {expanded && payment.authorization_response_code != null && (
          <p className="amount small">
            ARC: {payment.authorization_response_code}
          </p>
        )}

        {expanded &&
          Array.isArray(payment.credit_references) &&
          payment.credit_references.length > 0 && (
            <div className="payment-details">
              <p className="amount small">
                {translate(lang, "Brukt gavekort: ", "Paid with gift card: ")}
                {payment.credit_references.map((ref) => ref.value).join(", ")}
              </p>
            </div>
          )}

        {payment.point_scheme && (
          <p className="amount small">
            {translate(
              lang,
              "Betalte med lojalitetspoeng",
              "Paid with loyalty points"
            )}
            : {payment.point_scheme.points}
            {payment.point_scheme.points_name &&
              `(${payment.point_scheme.points_name})`}
          </p>
        )}

        {expanded && payment.tip_amount != null && (
          <p className="amount small">
            {translate(lang, "Tips", "Tip")}: {formatCurrency(
              payment.tip_amount,
              payment.currency,
              true
            )}
          </p>
        )}

        {expanded && payment.payment_amount != null && (
          <p className="amount small">
            {translate(lang, "Betalingsbeløp", "Payment amount")}:{" "}
            {formatCurrency(
              payment.payment_amount,
              payment.currency,
              true
            )}
          </p>
        )}
      </div>
    </div>
  );
};
